import React, {useEffect, useState} from 'react';
import {Box, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useForm} from 'react-hook-form';
import {ISummary} from './interfaces/summary.interfaces';
import OrderSummary from './components/OrderSummary';
import ReadonlyInput from './components/ReadyOnlyInput';
import {defaultIGTF, defaultIVA} from '../../../../../../constants/accounting';
import {useSalesmanContext} from '../../../../../../context/salesman';
import {CustomerIcon} from '../../../../../../components/icons';
import InternalTemplate from '../../../../../../components/templates/internal';
import {StackParamList} from '../../../../../../navigators';
import {SpinnerModal} from '../../../../../../components/modals';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../components/buttons';
import {DropDownInput} from '../../../../../../components/inputs';
import {getDropDownOptions} from '../../../../../../utils/ui/copy-paste/get-dropdown-options';
import {CurrencyOptionList} from '../../../../../../constants/options';
import {CurrencyOptionEnum} from '../../../../../../constants/enumerations';
import useToast from '../../../../../../hooks';
import {
  RESULT_REQUEST_CACHED_FAILED,
  RESULT_REQUEST_CACHED_OK,
} from '../../../../../../interfaces/types/result-of-request-cached';

type ScreenProps = NativeStackScreenProps<StackParamList, 'SalesmanOrderBegin'>;

const SalesmanOrderBeginScreen = ({navigation, route}: ScreenProps) => {
  const {fromTemplate} = route.params;
  const {
    customerPaymentOptions: paymentOptions,
    client,
    customerOrder,
    resetNewOrder,
    sendSalesmanNewOrder: sendOrder,
    clearSalesmanProducts: clearProductList,
  } = useSalesmanContext();

  const [summary, setSummary] = useState({
    count: 0,
    subTotal: 0,
    iva: 0,
    total: 0,
    igtf: 0,
    date: new Date(),
    client: {
      fullName:
        customerOrder && customerOrder.client ? customerOrder.client.name : '',
    },
  } as ISummary);

  const [isLoading, setIsLoading] = useState(false);

  const getDefaultCurrency = (): CurrencyOptionEnum | null => {
    return customerOrder && customerOrder.foreign === true
      ? CurrencyOptionEnum.foreign
      : CurrencyOptionEnum.local;
  };

  const getDefaultPaymentType = () => {
    return customerOrder && customerOrder.paymentOptionId
      ? customerOrder.paymentOptionId
      : '';
  };

  const getDefaultPriceListOption = () => {
    return customerOrder && customerOrder.priceListId
      ? customerOrder.priceListId
      : '';
  };

  const {showServerError, showOrderError} = useToast();

  const hasItems = () => {
    return (
      customerOrder && customerOrder.items && customerOrder.items.length > 0
    );
  };

  const {
    watch,
    setValue,
    control,
    formState: {errors},
  } = useForm({
    defaultValues: {
      paymentType: fromTemplate && hasItems() ? getDefaultPaymentType() : '',
      currency: fromTemplate && hasItems() ? getDefaultCurrency() : '',
      priceId: fromTemplate && hasItems() ? getDefaultPriceListOption() : '',
      requestTransportQuote: false,
      defineTransportProvider: false,
    },
  });

  const paymentType: string | null = watch('paymentType');
  const currency: CurrencyOptionEnum | null | string = watch('currency');
  const priceId: string | null = watch('priceId');
  const requestTransportQuote: boolean = watch('requestTransportQuote');
  const defineTransportProvider: boolean = watch('defineTransportProvider');

  const getItemCount = () => {
    let itemCount = 0;
    if (customerOrder && customerOrder?.items) {
      customerOrder?.items.forEach(item => (itemCount += item.count));
    }
    return itemCount;
  };

  const getSubTotal = () => {
    let subTotal = 0;
    if (customerOrder && customerOrder?.items) {
      customerOrder?.items.forEach(
        item => (subTotal += item.count * item.price),
      );
    }
    return subTotal;
  };

  const getIva = (subTotal: number) => {
    if (customerOrder) {
      return subTotal * defaultIVA;
    }
    return 0;
  };

  const getIgtf = (total: number) => {
    if (customerOrder) {
      return total * defaultIGTF;
    }
    return 0;
  };

  const updateSummary = () => {
    if (customerOrder && customerOrder.client) {
      const subTotal = getSubTotal();
      const iva = getIva(subTotal);
      const total = iva + subTotal;
      const igtf = getIgtf(total);
      const count = getItemCount();

      setSummary({
        count,
        subTotal,
        iva,
        total,
        igtf,
        date: customerOrder.date as Date,
        client: {
          fullName: customerOrder.client.name!,
        },
      });
    }
  };

  useEffect(() => {
    setIsLoading(false);
    updateSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, customerOrder]);

  useEffect(() => {
    setValue('defineTransportProvider', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestTransportQuote]);

  useEffect(() => {
    if (customerOrder) {
      if (customerOrder.foreign !== checkIsForeign()) {
        setValue('priceId', '');
      }
    } else {
      setValue('priceId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  useEffect(() => {
    restartScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceId, paymentType]);

  const checkIsForeign = () => {
    return currency === CurrencyOptionEnum.foreign;
  };

  const handleGoBack = () => {
    navigation.pop(1);
  };

  const updateOrder = () => {
    if (customerOrder && paymentType && priceId && currency) {
      customerOrder.paymentOptionId = paymentType;
      customerOrder.priceListId = priceId;
      customerOrder.foreign = currency === CurrencyOptionEnum.foreign;
      customerOrder.requestTransportQuote = requestTransportQuote ?? false;
    }
  };

  const handleOnContinuePress = async () => {
    updateOrder();
    if (defineTransportProvider) {
      navigation.navigate('SalesmanOrderEnd');
      return;
    }
    try {
      setIsLoading(true);
      if (customerOrder) {
        const result = await sendOrder(customerOrder);

        setIsLoading(false);
        if (result === RESULT_REQUEST_CACHED_OK) {
          navigation.navigate('SalesmanOrderSuccess');
        } else if (result === RESULT_REQUEST_CACHED_FAILED) {
          showOrderError();
        } else {
          const {request} = result;

          navigation.navigate('WithoutConnectionConfirm', {
            request,
            workflow: 'salesman-order',
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      showServerError();
    }
  };

  const handleOnProductsPress = () => {
    updateOrder();
    navigation.navigate('SalesmanOrderShoppingCart');
    if (customerOrder?.items?.length === 0) {
      navigation.navigate('SalesmanOrderAdd');
    }
  };

  const handleClearOrder = () => {
    resetNewOrder();
    restartScreen();
  };

  const restartScreen = () => {
    updateSummary();
    clearProductList();
  };

  const showFormButtons = () => {
    return paymentType && priceId && currency && hasItems();
  };

  const showProductButton = () => {
    return paymentType && priceId && currency;
  };

  const getProductsButtonLabel = () => {
    if (customerOrder?.items?.length === 0) {
      return 'salesman-order-shopping-car-products-button';
    }

    return 'salesman-order-begin-products-button';
  };

  const getPriceListOptions = () => {
    if (client && client.priceOptionList && currency !== null) {
      const isForeign = currency === CurrencyOptionEnum.foreign;
      return getDropDownOptions(
        client.priceOptionList.filter(
          priceList => priceList.foreign === isForeign,
        ),
      );
    }
    return [];
  };

  const handleTransportQuoteToogle = (toogleValue: boolean) => {
    setValue('requestTransportQuote', toogleValue);
  };

  const handleTransportProviderToogle = (toogleValue: boolean) => {
    setValue('defineTransportProvider', toogleValue);
  };

  return (
    <InternalTemplate
      title={'salesman-order-begin-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />
      <VStack paddingX={5} marginBottom={200}>
        <ReadonlyInput
          icon={<CustomerIcon width={25} />}
          label="Cliente"
          value={summary.client.fullName}
        />
        <DropDownInput
          control={control}
          name="paymentType"
          label="salesman-order-begin-payment-condition"
          options={paymentOptions ? getDropDownOptions(paymentOptions) : []}
          defaultValue={getDefaultPaymentType()}
          error={errors.paymentType}
          rules={{
            required: 'form-is-required',
          }}
        />
        <DropDownInput
          control={control}
          name="currency"
          label="salesman-order-begin-currency-condition"
          options={CurrencyOptionList}
          error={errors.currency}
          defaultValue={getDefaultCurrency()}
          rules={{
            required: 'form-is-required',
          }}
        />
        <DropDownInput
          control={control}
          name="priceId"
          label="salesman-order-begin-price-condition"
          options={getPriceListOptions()}
          error={errors.priceId}
          defaultValue={getDefaultPriceListOption()}
          rules={{
            required: 'form-is-required',
          }}
        />

        {showProductButton() && (
          <Box
            pt={2}
            pb={2}
            borderBottomColor={'#D9EDFF'}
            borderTopColor={'#D9EDFF'}>
            <PrimaryButton
              label={getProductsButtonLabel()}
              onPress={handleOnProductsPress}
            />
          </Box>
        )}
        {showFormButtons() && (
          <Box marginBottom={5}>
            <SecondaryButton
              label="salesman-order-begin-form-button-reset"
              onPress={handleClearOrder}
            />
          </Box>
        )}

        {hasItems() && (
          <OrderSummary
            currency={getDefaultCurrency()}
            summary={summary}
            defineTransportProvider={defineTransportProvider}
            requestTransportQuote={requestTransportQuote}
            onTransporteQuoteToogle={handleTransportQuoteToogle}
            onTransportProviderToogle={handleTransportProviderToogle}
            onContinueToogle={handleOnContinuePress}
          />
        )}
      </VStack>
    </InternalTemplate>
  );
};
export default SalesmanOrderBeginScreen;
